import { api, ApiError } from "./api.js";
import type { PortalAccess } from "./portalAccess.js";
import type { PortalModule, PortalModuleGrant } from "@wa/core/portal-modules";
import type { PortalProjectTab, PortalProjectTabGrant } from "@wa/core/portal-project-tabs";

export type LeadStatus = "new" | "contacted" | "qualified" | "proposal" | "won" | "lost";
export type ClientKind = "company" | "person";
export type ClientStatus = "active" | "inactive";

export interface Lead {
  id: string;
  name: string;
  kind: ClientKind;
  regCode: string | null;
  vatNumber: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  source: string | null;
  status: LeadStatus;
  assignedTo: string | null;
  valueCents: number | null;
  notes: string | null;
  convertedClientId: string | null;
  convertedAt: string | null;
  /**
   * WHERE the record came from, as opposed to `source`, which says which tool
   * made it. Absolute URL of the page it converted on, the referrer HOST only
   * (never the full referring URL - that carries somebody's search query), and
   * one reader-facing campaign line built from the utm parameters at capture.
   *
   * Null on every record created before the columns existed and on every one
   * typed in by hand, which is why `LeadOrigin` renders nothing rather than
   * three "unknown" rows.
   */
  sourceUrl?: string | null;
  sourceRef?: string | null;
  sourceCampaign?: string | null;
  /**
   * The desk's own labels. The same vocabulary `Client.tags` holds, and they
   * travel across a conversion - see the API's `leads.routes.ts`, which copies
   * them onto the new client row rather than leaving them on a record nobody
   * looks at again.
   */
  tags: string[];
  /**
   * The earliest OUTSTANDING step in this record's timeline, or null.
   *
   * Computed by the list route, so it is absent on a record fetched any other
   * way. Null means nothing is planned; a date in the past means something was
   * planned and is late. Those are different states and `nextStepState` in
   * `lib/nextStep.ts` is what tells them apart - a null and a past date must not
   * be drawn the same way.
   */
  nextStepAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  id: string;
  kind: ClientKind;
  name: string;
  regCode: string | null;
  vatNumber: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  addressLine: string | null;
  city: string | null;
  zip: string | null;
  country: string;
  status: ClientStatus;
  currency: string;
  paymentTermsDays: number | null;
  /**
   * What language this client's documents are written in, or null for "whatever
   * the desk's default is".
   *
   * Null is not the same as "et": it means nobody has chosen, so the desk
   * default reaches this client the day it moves. Optional on the type because
   * a cached bundle can outlive the API that added the column.
   */
  locale?: string | null;
  assignedTo: string | null;
  notes: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  contacts?: Contact[];
  activities?: Activity[];
  _count?: { contacts: number; activities: number; invoices?: number };
  /** What this client has actually paid, summed off Payment. List endpoint only. */
  revenueCents?: number;
  /** The earliest OUTSTANDING step, list endpoint only. Same three-state rule as
   *  `Lead.nextStepAt` - see `lib/nextStep.ts`. */
  nextStepAt?: string | null;
  /** How many of this client's people can get into the portal, and how many are
   *  still holding an invite. List endpoint only - see `lib/portalAccess.ts`,
   *  where the three states this collapses into are named. */
  portal?: PortalAccess;
  /**
   * This company's own portal grant, or `null` for "did not decide, inherit".
   *
   * DETAIL endpoint only, like the two fields under it: the list route ships
   * hundreds of rows and none of its screens edit a grant, so it does not pay
   * for the settings read that `portalDefaults` needs. Absent here therefore
   * means "not asked for", NOT "unrestricted" - a screen that treats a missing
   * field as `null` on a list row is claiming a decision it never fetched.
   *
   * REPLACES the default rather than narrowing it, upwards included. See
   * `resolveCompanyGrant` in `@wa/core/portal-modules`, which is the only place
   * that rule exists.
   */
  portalModules?: PortalModuleGrant;
  /** Which tabs this company's project pages draw, or `null` to inherit. Detail
   *  endpoint only. Two levels, no per-contact third - `overview` can never be
   *  switched off (`PORTAL_PROJECT_TAB_FLOOR`). */
  portalProjectTabs?: PortalProjectTabGrant;
  /**
   * The CRM-wide default both fields above fall back to (the `crm.portal`
   * Setting), carried on the detail response so the client screen can say what
   * an inherited grant actually opens.
   *
   * Without it, a client row of `null` would have to be drawn as "everything",
   * which is a lie at any installation that set a default - and an overstated
   * access screen is one an operator stops double-checking.
   */
  portalDefaults?: { modules: PortalModuleGrant; projectTabs: PortalProjectTabGrant };
}

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  /**
   * When the portal last served this person, or null for "never opened it".
   *
   * Null is an answer, not missing data. A contact who accepted the invite and
   * never came back looks identical to one who uses the portal weekly unless
   * this is drawn - and in Perfex, where the same portal stood open for seven
   * years, that was 36 of 38 people.
   */
  lastSeenAt: string | null;
}

export interface Contact {
  id: string;
  clientId: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  position: string | null;
  isPrimary: boolean;
  createdAt: string;
  userId?: string | null;
  /** The portal user, when the contact is linked to one - added by the contacts GET. */
  portalUser?: PortalUser | null;
  /** A pending portal invite (an account-less contact holding a valid invite). */
  portalInvitePending?: boolean;
  /**
   * When the newest unredeemed invitation ran out, or null.
   *
   * A separate fact from `portalInvitePending`, and the pair is the point:
   * "nobody has been asked" and "we asked and nobody came" are different
   * answers and lead to different next moves. In Perfex, where the same portal
   * stood open for seven years, 2 contacts of 38 ever signed in - a row that
   * prints nothing for a lapsed invitation throws away the half of that number
   * worth knowing.
   */
  portalInviteLapsedAt?: string | null;
  /**
   * Which portal modules this person may open, or `null` for "never restricted".
   *
   * Null is not an empty list. Every contact linked before per-module grants
   * existed holds null and sees everything, which is what the operator already
   * granted them; an empty array is the operator deliberately closing every
   * door on somebody who keeps their sign-in. The screens have to draw the two
   * differently or the second state reads as a bug.
   */
  portalModules?: PortalModuleGrant;
  /**
   * What the portal will ACTUALLY open for this person, spelled out.
   *
   * Not the same fact as `portalModules` above, and the pair is the point. That
   * one is the row the editor edits - this person's own opinion, `null` when
   * they hold none. This one is the answer the gate gives: the CRM-wide default
   * and the company override resolved, then intersected with that row
   * (`resolveModuleGrant`). So a contact with `portalModules: null` at a company
   * sold two modules has `portalModulesEffective` of length two, and a contact
   * holding an explicit list of three at a company that has since closed all
   * three has an EMPTY one - a person who can sign in and open nothing.
   *
   * Never `null`: it is a resolved answer, not a decision, so "unrestricted"
   * arrives here as the whole vocabulary rather than as an absence. Computed
   * server-side on purpose - a second copy of the precedence rule in the browser
   * is a copy nothing can check against the first.
   *
   * Sent by the client DETAIL route and by the contacts list route. Absent on a
   * contact that came back from a write route.
   */
  portalModulesEffective?: PortalModule[];
}

export interface Activity {
  id: string;
  clientId: string | null;
  leadId: string | null;
  type: string;
  body: string | null;
  actor: string | null;
  /** When this entry is something somebody still has to DO. */
  dueAt: string | null;
  /** When it was carried out. A column rather than a cleared `dueAt`, so "we
   *  said we would ring them on Tuesday" survives the ringing - see the schema
   *  comment on `Activity.doneAt`. Outstanding is `dueAt && !doneAt`. */
  doneAt: string | null;
  createdAt: string;
  /** The call that produced the entry, when the phone did. Set by the voicemail
   *  intake; null on everything typed or imported. */
  callLogId?: string | null;
}

export type TimelineKind =
  | "note" | "email" | "call" | "proposal" | "estimate" | "invoice" | "payment"
  | "credit_note" | "expense" | "project" | "task" | "contract" | "ticket" | "package"
  /** Something the CLIENT did in the portal themselves. Sent as an activity, in
   *  a lane of its own so it can be asked about separately. */
  | "portal";

export interface TimelineEvent {
  id: string;
  kind: TimelineKind;
  at: string;
  title: string;
  subtitle: string | null;
  status: string | null;
  amountCents: number | null;
  currency: string | null;
  actor: string | null;
  link: string | null;
}

export interface ClientTimeline {
  rows: TimelineEvent[];
  total: number;
  counts: Partial<Record<TimelineKind, number>>;
}

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return "";
  const p = Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

// ── Leads ─────────────────────────────────────────────────────────────────
export const getLeads = (params?: Record<string, string | number | undefined>) =>
  api.get<{ rows: Lead[]; total: number; pageCount: number }>(`/admin/crm/leads${qs(params)}`);

export const getLead = (id: string) =>
  api.get<Lead & { activities: Activity[] }>(`/admin/crm/leads/${id}`);

export const createLead = (body: Partial<Lead>) =>
  api.post<Lead & { warnings?: CrmDuplicateWarning[] }>("/admin/crm/leads", body);

export const updateLead = (id: string, body: Partial<Lead>) =>
  api.patch<Lead>(`/admin/crm/leads/${id}`, body);

export const deleteLead = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/leads/${id}`);

export const convertLead = (id: string) =>
  api.post<{ ok: boolean; client: Client }>(`/admin/crm/leads/${id}/convert`, {});

/** The labels already in use on leads - the options the board's filter offers. */
export const getLeadTags = () => api.get<{ rows: string[] }>("/admin/crm/lead-tags");

/**
 * File an entry against a lead - a note, or a planned next step.
 *
 * `dueAt` is what makes it a plan rather than a record. The route has accepted
 * the field since the module was written; this signature did not carry it, and
 * nothing called this function at all, which is why "what happens next with this
 * company" was a question the CRM could not answer.
 */
export const addLeadActivity = (
  id: string,
  body: { type: string; body?: string; actor?: string; dueAt?: string },
) => api.post<Activity>(`/admin/crm/leads/${id}/activities`, body);

// ── Clients ───────────────────────────────────────────────────────────────
export const getClients = (params?: Record<string, string | number | undefined>) =>
  api.get<{ rows: Client[]; total: number; pageCount: number }>(`/admin/crm/clients${qs(params)}`);

export const getClient = (id: string) =>
  api.get<Client & { contacts: Contact[]; activities: Activity[] }>(`/admin/crm/clients/${id}`);

export const createClient = (body: Partial<Client>) =>
  api.post<Client & { warnings?: CrmDuplicateWarning[] }>("/admin/crm/clients", body);

export const updateClient = (id: string, body: Partial<Client>) =>
  api.patch<Client>(`/admin/crm/clients/${id}`, body);

export const deleteClient = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/clients/${id}`);

/**
 * One client's people, primary first.
 *
 * The same rows `getClient` already carries, asked for on their own so a screen
 * that only wants the list does not pull the client's activities and invoices
 * with it. Behind `view:crm`, like every other read in this module.
 */
export const getClientContacts = (clientId: string) =>
  api.get<{ rows: Contact[] }>(`/admin/crm/clients/${clientId}/contacts`);

export const addContact = (clientId: string, body: Partial<Contact>) =>
  api.post<Contact>(`/admin/crm/clients/${clientId}/contacts`, body);

export const updateContact = (clientId: string, cid: string, body: Partial<Contact>) =>
  api.patch<Contact>(`/admin/crm/clients/${clientId}/contacts/${cid}`, body);

export const deleteContact = (clientId: string, cid: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/clients/${clientId}/contacts/${cid}`);

/** Link a contact to a portal user (by the contact's own e-mail by default). */
export const linkContactPortal = (clientId: string, cid: string, email?: string) =>
  api.post<Contact & { portalUser: PortalUser | null }>(
    `/admin/crm/clients/${clientId}/contacts/${cid}/portal-access`,
    email ? { email } : {},
  );

/**
 * Take a contact's portal access away - BOTH ways in.
 *
 * A person can reach the client's records through a linked account or through
 * an invitation still sitting in their inbox. This closes each one it finds and
 * says which, because "access removed" on a contact who had none is a message
 * that teaches the operator to distrust the next one.
 */
export const unlinkContactPortal = (clientId: string, cid: string) =>
  api.del<Contact & { portalUser: null; revoked: { link: boolean; invites: number } }>(
    `/admin/crm/clients/${clientId}/contacts/${cid}/portal-access`,
  );

/**
 * The invite answer: either linked straight away (the User existed) OR an invite
 * e-mail went out.
 *
 * `heldBy` and `sendFailed` are what the invite branch says about DELIVERY, and
 * the screen has to repeat them. With mail test mode on, the invite is
 * readdressed to the test inbox and the client never sees it - an unqualified
 * "invite sent" there is an operator waiting on a reply that cannot arrive.
 */
export type PortalInviteResult =
  | (Contact & { portalUser: PortalUser; linked: true })
  | {
      invited: true;
      email: string;
      /** Test mode redirected the invite to this inbox instead of the client. */
      heldBy?: string;
      /** The invite was minted but the mail did not go out at all. */
      sendFailed?: boolean;
    };

/** Grant portal access: link now (when a User holds that e-mail) or send an invite. */
export const inviteContactPortal = (clientId: string, cid: string, locale?: string) =>
  api.post<PortalInviteResult>(
    `/admin/crm/clients/${clientId}/contacts/${cid}/portal-invite`,
    locale ? { locale } : {},
  );

/**
 * Set which portal modules a contact may open. `null` clears the restriction.
 *
 * A PUT rather than a PATCH: the body is the whole grant, so a screen that
 * unticks the last box sends `[]` and means it. A partial update would have no
 * way to express "close everything" that a dropped field could not also mean.
 */
export const setContactPortalModules = (clientId: string, cid: string, modules: PortalModuleGrant) =>
  api.put<Contact & { portalUser: PortalUser | null }>(
    `/admin/crm/clients/${clientId}/contacts/${cid}/portal-modules`,
    { modules },
  );

/**
 * Set which portal modules a whole COMPANY may open. `null` inherits the
 * CRM-wide default.
 *
 * One level above `setContactPortalModules`, and `null` means something
 * different here: on a contact it is "this person's row narrows nothing", on a
 * client it is "this company did not decide". The two coincide only while no
 * default exists, which is why the answer carries the effective list beside the
 * stored one - a screen that echoed only the column would print "everything"
 * over a default that closed half of it.
 *
 * The response is the pair and NOT a whole client row: nothing else on the
 * client changed, and returning a partial `Client` would invite a caller to
 * spread it over the one they are holding and blank the fields the route never
 * selected.
 */
export const setClientPortalModules = (clientId: string, modules: PortalModuleGrant) =>
  api.put<{ portalModules: PortalModuleGrant; portalModulesEffective: PortalModule[] }>(
    `/admin/crm/clients/${clientId}/portal-modules`,
    { modules },
  );

/**
 * Set which tabs this company's project pages draw. `null` inherits the default.
 *
 * Presentation rather than permission: every record behind every tab is still
 * gated by its module, so closing a tab hides a panel while closing a module
 * closes a door. There is deliberately no per-contact counterpart - two people
 * at one company must not see different panels of the same project.
 *
 * `portalProjectTabsEffective` comes back through `grantedProjectTabs`, so it
 * always carries the floor: a company stripped of every tab still gets the
 * overview, and the screen has to show that rather than promise a page with
 * nothing on it.
 */
export const setClientPortalProjectTabs = (clientId: string, tabs: PortalProjectTabGrant) =>
  api.put<{ portalProjectTabs: PortalProjectTabGrant; portalProjectTabsEffective: PortalProjectTab[] }>(
    `/admin/crm/clients/${clientId}/portal-project-tabs`,
    { tabs },
  );

export const addClientActivity = (
  clientId: string,
  body: { type: string; body?: string; actor?: string; dueAt?: string },
) => api.post<Activity>(`/admin/crm/clients/${clientId}/activities`, body);

/**
 * Move or complete a planned step. One route for either party - `Activity` is
 * one table, and an entry follows a lead across its conversion.
 *
 * `null` clears: `doneAt: null` un-completes a step somebody ticked off the
 * wrong row, and `dueAt: null` turns a plan back into an ordinary note.
 */
export const updateActivity = (id: string, body: { dueAt?: string | null; doneAt?: string | null }) =>
  api.patch<Activity>(`/admin/crm/activities/${id}`, body);

export const deleteActivity = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/activities/${id}`);

// ── Client notification preferences (e-mail, per module) ─────────────────────
export interface ClientNotificationCategory { key: string; enabled: boolean }

export const getClientNotifications = (clientId: string) =>
  api.get<{ categories: ClientNotificationCategory[] }>(`/admin/crm/clients/${clientId}/notifications`);

export const setClientNotifications = (clientId: string, prefs: ClientNotificationCategory[]) =>
  api.put<{ categories: ClientNotificationCategory[] }>(`/admin/crm/clients/${clientId}/notifications`, { prefs });

/** The client activity log - every event tied to the client, on one timeline. */
export const getClientTimeline = (clientId: string, params?: { limit?: number }) =>
  api.get<ClientTimeline>(`/admin/crm/clients/${clientId}/timeline${qs(params)}`);

// ── "Do we already hold this company?" ────────────────────────────────────
// Asked by every screen that offers to file a company the CRM did not create
// itself - the register profile above all. Without it the offer is made blind
// and the same business ends up in the pipeline twice.

export interface CrmLeadMatch {
  id: string;
  name: string;
  status: LeadStatus;
  convertedClientId: string | null;
  createdAt: string;
}
export interface CrmClientMatch {
  id: string;
  name: string;
  status: ClientStatus;
  createdAt: string;
}
export interface CrmLookup {
  regCode: string;
  leads: CrmLeadMatch[];
  clients: CrmClientMatch[];
}

/** Leads and clients carrying this registry code, oldest first. */
export const lookupByRegCode = (regCode: string, signal?: AbortSignal) =>
  api.get<CrmLookup>(`/admin/crm/lookup?regCode=${encodeURIComponent(regCode)}`, signal);

/**
 * What the create routes report about the OTHER table.
 *
 * Not an error: the row was written. A lead for a company that is already a
 * client is sometimes the right thing to file, so the server allows it and says
 * so, and the screen turns it into a link to the record that exists. `rule`
 * separates the two keys: `reg_code` is certain, `name` is probable.
 */
export interface CrmDuplicateWarning {
  kind: "existing_client" | "existing_lead";
  id: string;
  name: string;
  rule: "reg_code" | "name";
}

/**
 * The 409 `createLead`/`createClient` answer with when the registry code is
 * already taken. Returns the record that holds it, so a caller can open the row
 * that exists instead of reporting a failure the operator cannot act on.
 */
export interface DuplicateRegCode {
  leadId: string | null;
  clientId: string | null;
  name: string;
}
export function duplicateRegCode(err: unknown): DuplicateRegCode | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { error?: string; leadId?: string; clientId?: string; name?: string } | null;
  if (!body || body.error !== "duplicate_reg_code") return null;
  return { leadId: body.leadId ?? null, clientId: body.clientId ?? null, name: body.name ?? "" };
}
